import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import type { EnhancedStore } from '@reduxjs/toolkit';

import { getGlobalHeaders } from './headers';
import { requestDone, requestError, requestStart } from './reducer';

const DEFAULT_TIMEOUT_MS = 30_000;

let reduxStore: EnhancedStore | null = null;
const clients = new Map<string, AxiosInstance>();

export function setReduxStoreForHttpClient(store: EnhancedStore): void {
  reduxStore = store;
}

function ensureStore(): EnhancedStore {
  if (!reduxStore) {
    throw new Error(
      'Redux store must be set before creating an HTTP client. Call setReduxStoreForHttpClient(store) first.',
    );
  }
  return reduxStore;
}

function attachInterceptors(client: AxiosInstance, store: EnhancedStore): void {
  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    store.dispatch(requestStart());
    return config;
  });
  client.interceptors.response.use(
    (response) => {
      store.dispatch(requestDone());
      return response;
    },
    (error) => {
      store.dispatch(requestError());
      return Promise.reject(error);
    },
  );
}

export function createHttpClient(
  identifier: string,
  baseURL: string,
  headers: Record<string, string> = {},
): AxiosInstance {
  const existing = clients.get(identifier);
  if (existing) return existing;

  const store = ensureStore();
  const client = axios.create({
    baseURL,
    timeout: DEFAULT_TIMEOUT_MS,
    headers: { ...getGlobalHeaders(), ...headers },
  });
  attachInterceptors(client, store);
  clients.set(identifier, client);
  return client;
}

export function getHttpClient(identifier: string): AxiosInstance {
  const client = clients.get(identifier);
  if (!client) {
    throw new Error(
      `No HTTP client registered for identifier "${identifier}". Call createHttpClient first.`,
    );
  }
  Object.assign(client.defaults.headers, getGlobalHeaders());
  return client;
}

/** Test-only: clear the registry between tests. Not for production code. */
export function __resetHttpClientsForTests(): void {
  clients.clear();
  reduxStore = null;
}
